/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import cx from 'classnames';
import { Pagination } from 'sarsaparilla';

import { paginator } from 'shared-ui/utils/paginator';
import { useUserLocation } from 'shared-ui/geolocation';
import { CompactSearchResultCard } from 'shared-ui/search-result-card';

import { SearchFilterCombo } from '../SearchFilterCombo/SearchFilterCombo';

import { type BookingType, useFiltersState } from '../FiltersContext';
import { useSearchResultsQuery } from '../api/search.queries';

import styles from './ResultsList.module.scss';
import { PlaceholderList } from './ResultsListSkeleton';

export function ResultsList() {
    const [, setFilters] = useFiltersState();
    const [activePageIndex, setActivePageIndex] = React.useState(0);
    const { data, isPending, isFetching, error } = useSearchResultsQuery();
    const heading = useSearchResultsHeading();

    const { paginatedData, totalPages } = React.useMemo(() => {
        return paginator({
            items: data?.results ?? [],
            activeIndex: activePageIndex,
            perPage: 5,
        });
    }, [data?.results, activePageIndex]);

    // When the data changes, reset pagination to 0
    React.useEffect(() => {
        setActivePageIndex(0);
    }, [data?.results]);

    if (error) {
        return <div className="text-md">Error getting search results</div>;
    }

    return (
        <div className={styles.resultsList}>
            <div className={styles.searchGroup} role="search" aria-label="Map filter">
                <SearchFilterCombo />
            </div>

            <div className="mt-2 mb-4">
                <h3 className="text-sm font-weight-normal color-text-02 text-center">
                    {heading}
                </h3>
            </div>

            {/* Placeholders come before the empty state so an in-flight (or not
                yet started) search never reads as "no results". */}
            {isPending || isFetching ? (
                <div className={cx(styles.list, 'my-4')}>
                    <PlaceholderList />
                </div>
            ) : !data?.results || data.results.length === 0 ? (
                <div>
                    <h2 className="text-lg font-weight-semibold">
                        Sorry, no results found
                    </h2>
                    <p className="text-md">
                        Try removing filters, searching a different location, or use the
                        map to broaden your search a larger area.
                    </p>
                </div>
            ) : (
                <>
                    <div className={styles.list} role="list">
                        {paginatedData.map((item) => {
                            return (
                                <div key={item.id} role="listitem">
                                    <CompactSearchResultCard
                                        item={item}
                                        gaTrackingId="698514527793"
                                        onHover={(cardData) => {
                                            setFilters({ hoveredCardId: cardData.id });
                                        }}
                                        onHoverLeave={() => {
                                            setFilters({ hoveredCardId: null });
                                        }}
                                    />
                                </div>
                            );
                        })}
                    </div>

                    {totalPages > 1 && (
                        <div className={styles.pagination}>
                            <Pagination
                                pages={totalPages}
                                activePage={activePageIndex}
                                onChange={(newPage) => setActivePageIndex(newPage)}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

const formatter = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
});

/**
 * Returns the label for the experiences/results type, singular or plural based on count.
 */
const getExperiencesText = (
    bookingType: BookingType | null | undefined,
    count: number
): string => {
    const isSingular = count === 1;

    switch (bookingType) {
        case 'camping':
            return isSingular ? 'place to stay' : 'places to stay';
        case 'dayUse':
        case 'venues':
        case 'venuesAndDayUse':
            return isSingular ? 'facility' : 'facilities';
        case 'tickets':
        case 'activityPass':
        case 'permits':
        case 'treePermits':
        case 'permitsAndPasses':
            return isSingular ? 'thing to do' : 'things to do';
        case 'poi':
        case 'recArea':
            return isSingular ? 'recreation area' : 'recreation areas';
        default:
            return isSingular ? 'experience' : 'experiences';
    }
};

/**
 * Builds the search results heading string: count + experiences label + optional location.
 */
function useSearchResultsHeading() {
    const [filters] = useFiltersState();
    const { data, isFetching } = useSearchResultsQuery();
    const userLocation = useUserLocation();

    if (isFetching) return 'Searching …';
    if (!data) return 'Search Results';

    const total = data?.total ?? 0;

    const countPart =
        total >= 10000 ? 'Over 10,000' : total > 0 ? formatter.format(total) : 'No';

    const location = data.location || filters.location;

    const isLocationNearby =
        !filters.location &&
        filters.lat === userLocation?.latitude &&
        filters.lng === userLocation?.longitude;

    const locationPart = location
        ? `near ${location.split(',')[0]}`
        : isLocationNearby
          ? 'nearby'
          : 'within map area';
    const experiencesPart = getExperiencesText(filters.bookingType, data?.total ?? 0);

    return `${countPart} ${experiencesPart} ${locationPart}`;
}
